/**
 * Session/token helpers.
 *
 * Two rules govern how long a login lasts:
 *
 * 1. The session is stored in `sessionStorage`, which the browser discards when the tab closes.
 *    A reload keeps the user signed in; closing the tab signs them out, and so does a crash or a
 *    force-quit, since nothing is left behind to restore. The tradeoff of tab-scoped storage is
 *    that each tab holds its own session, so opening the app in a fresh tab means signing in again.
 *
 * 2. The backend issues a JWT with a `JWT_EXPIRE` lifetime (7d by default). Nothing used to notice
 *    when that lifetime ran out: the token sat in storage, `ProtectedRoute` kept rendering the app,
 *    and every request quietly failed with 401 until the user logged out by hand. These helpers
 *    read the token's own `exp` claim so the app can drop an expired session on its own.
 *
 * This module must not import from `contexts/` or `utils/api` — both of those import back into it.
 */

/** Dispatched after login/logout so cached lists reload when auth changes (same-tab SPA navigation). */
export const AUTH_CHANGE_EVENT = 'reference-data-auth';

/** Query flag added to /login so the page can explain why the user landed back there. */
export const SESSION_EXPIRED_PARAM = 'expired';

/** Treat a token as expired slightly early, so a request is not sent with a token about to lapse. */
const EXPIRY_SKEW_MS = 5000;

/** setTimeout overflows past ~24.8 days and fires immediately; re-arm in chunks instead. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** @returns {object|null} the persisted session user, or null when absent/corrupt. */
export const getStoredUser = () => {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/** @returns {string|null} the raw JWT from the persisted session user. */
export const getStoredToken = () => getStoredUser()?.token || null;

/**
 * Decode a JWT payload without verifying it. The signature is the server's business; the client
 * only needs `exp` to know when to stop pretending it is logged in.
 * @returns {object|null} the payload, or null when the token is missing/malformed.
 */
const decodeJwtPayload = (token) => {
  if (typeof token !== 'string') return null;
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
};

/**
 * @returns {number|null} epoch ms at which the token lapses, or null when it carries no `exp`
 * (a token without an expiry is never treated as expired by this module).
 */
export const getTokenExpiryMs = (token) => {
  const exp = decodeJwtPayload(token)?.exp;
  return Number.isFinite(exp) ? exp * 1000 : null;
};

/** @returns {boolean} true only when the token has an `exp` that has already passed. */
export const isTokenExpired = (token, skewMs = EXPIRY_SKEW_MS) => {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return false;
  return Date.now() >= expiryMs - skewMs;
};

/** @returns {boolean} true when a stored session exists but its token has lapsed. */
export const hasExpiredSession = () => {
  const token = getStoredToken();
  return Boolean(token) && isTokenExpired(token);
};

/**
 * Drop every per-user UI snapshot kept for the current browser session.
 *
 * List pages (Tasks, Matches, Teams & Events) stash their tab + filter selections in
 * sessionStorage so navigating to a detail page and back restores the view. Those snapshots
 * outlive a logout, so the next user to sign in on the same tab would inherit the previous
 * user's filters. Call this whenever the session identity changes.
 */
export const clearSessionScopedState = () => {
  try {
    sessionStorage.clear();
  } catch {
    // storage unavailable (private mode / blocked cookies) - nothing to clear
  }
};

/**
 * Remove the persisted session user and every per-session UI snapshot.
 * One sessionStorage wipe covers both, since the session user lives there too.
 */
export const clearAuthStorage = () => {
  clearSessionScopedState();
};

/**
 * Sessions used to live in localStorage, which survived tab closes. Drop any leftover copy so a
 * user carrying one over from a previous build starts fresh instead of staying signed in forever.
 */
const dropLegacyPersistentSession = () => {
  try {
    localStorage.removeItem('user');
  } catch {
    // storage unavailable - nothing to clear
  }
};

dropLegacyPersistentSession();

// Several places can notice the same dead session at once (a route render plus a few in-flight
// requests). Redirect once.
let logoutInFlight = false;

/**
 * Drop the session and send the browser back to the login page.
 *
 * Uses a location replace rather than router navigation so it can be called from non-component
 * code (the API layer) and so no stale in-memory state survives the transition.
 *
 * @param {{ expired?: boolean }} [options] - `expired` adds the flag that makes /login explain itself.
 */
export const forceLogout = ({ expired = false } = {}) => {
  if (logoutInFlight) return;
  logoutInFlight = true;

  clearAuthStorage();
  try {
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  } catch {
    // no window event support - the redirect below still resets everything
  }

  const target = expired ? `/login?${SESSION_EXPIRED_PARAM}=1` : '/login';
  if (window.location.pathname === '/login') {
    logoutInFlight = false;
    return;
  }
  window.location.replace(target);
};

/**
 * Watch the stored token and call `onExpire` the moment it lapses.
 *
 * Covers two ways a session can die while a tab sits open: the scheduled timer for the normal
 * case, and a focus/visibility re-check for machines that slept through the timer.
 *
 * @param {() => void} onExpire - invoked once when the session is found to be expired.
 * @returns {() => void} teardown function.
 */
export const watchSessionExpiry = (onExpire) => {
  let timerId = null;

  const clearTimer = () => {
    if (timerId !== null) {
      window.clearTimeout(timerId);
      timerId = null;
    }
  };

  const check = () => {
    clearTimer();

    const token = getStoredToken();
    if (!token) return;

    const expiryMs = getTokenExpiryMs(token);
    if (expiryMs === null) return;

    const remainingMs = expiryMs - EXPIRY_SKEW_MS - Date.now();
    if (remainingMs <= 0) {
      onExpire();
      return;
    }

    timerId = window.setTimeout(check, Math.min(remainingMs, MAX_TIMEOUT_MS));
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };

  check();
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    clearTimer();
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', onVisible);
  };
};
